import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Posts from '../../components/Posts/Posts';
import { Link } from 'react-router-dom';
import { getBlogPost } from '../../services/contentful';
import getQueryParams from '../../util/getQueryParams';
import Error from '../../components/Error/Error';
import Heading from '../../components/Heading/Heading';
import theme from '../../styles/theme';
import CTACard from '../../components/CTACard/CTACard';
import EmailSignup from '../../components/Shared/EmailSignup';
import Content from '../../components/Content/Content';
import FeaturedMedia from '../../components/Shared/FeaturedMedia';
import ReusePostSections from '../../components/Shared/ReusePostSections';
import PostDownloads from '../../components/Shared/PostDownloads';
import Disclaimer from '../../components/Disclaimer/Disclaimer';
import linkRenderer from '../../util/renderers/link';
import useGTM from '../../hooks/useGTM';
import responsiveImage from '../../util/renderers/responsiveImage';
import Styles from './ResourcePost.styled';
import { loadModel } from 'vmfcom-lib/utils';

function ResourcePost({ isLoading, error, autoplay, data }) {
  const entry = loadModel(data, 'VMFCom', 'BlogPostPage');
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();

  const posts = useMemo(() => {
    if (entry?.relatedPosts && entry.relatedPosts.length > 0) {
      return entry?.relatedPosts.map(
        ({ Title, contentTag, category, slug, featuredMedia }) => ({
          title: Title,
          category,
          contentTag,
          thumbnail: `${
            featuredMedia?.media?.file?.url || featuredMedia?.file?.url
          }?w=300`,
          mediaType: featuredMedia?.mediaType,
          slug,
        })
      );
    }

    return [];
  }, [entry]);

  const onEmailSubmit = () => {
    triggerGTMEvent({
      name: 'vmf.cta_click',
      location: 'Post Page',
      category: 'contact_us',
      action: 'form',
      label: 'email_form_submit',
    })();
  };

  const onPDFClick = () => {
    triggerGTMEvent({
      name: 'vmf.cta_click',
      location: 'Post Page',
      category: 'loan_education',
      action: 'button',
      label: 'download_pdf',
    })();
  };

  const formatter = new Intl.DateTimeFormat('en-us', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const formattedDate = entry?.publicationDate
    ? formatter.format(new Date(entry.publicationDate))
    : '';

  return (
    <Layout isLoading={isLoading}>
      <SEO
        title={entry?.titleTag}
        description={entry?.metaDescription}
        structuredData={JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: entry?.title,
          genre: entry?.category,
          description: entry?.metaDescription,
          image: [
            entry?.featuredMedia?.media?.fields?.file?.url ||
              entry?.featuredMedia?.thumbnail?.fields?.file?.url,
          ],
        })}
      />
      <Styles>
        {!entry?.copyBrief &&
          !entry?.featuredMedia &&
          !entry?.postContent1 &&
          !entry?.reusePostSections &&
          !entry?.postContent2 &&
          !entry?.postPdFs &&
          !entry?.disclaimer &&
          !entry?.posts &&
          !entry?.webForm && (
            <Content divider={false}>
              <p className='no-content'>This post has no content.</p>
            </Content>
          )}

        {entry?.copyBrief && (
          <Content className='title-caption' divider={false}>
            <Heading background={theme.colors.blue10} appearance='dark' />
            <h1>{entry?.Title}</h1>
          </Content>
        )}

        <Content className='author-date' divider={false}>
          <div>
            <b>{entry?.authorName}</b>
            <p>
              {' '}
              {formattedDate} | {entry?.readTime} minute read
            </p>
          </div>
        </Content>

        {entry?.featuredMedia && (
          <FeaturedMedia
            alt={entry?.featuredMedia?.altText}
            src={
              entry?.featuredMedia?.media?.file?.url ||
              entry?.featuredMedia?.thumbnail?.file?.url
            }
            mediaType={entry?.featuredMedia?.mediaType}
            video={{
              source: entry?.featuredMedia?.videoSource,
              content: entry?.featuredMedia?.embeddedContent,
            }}
            autoplay={autoplay}
          />
        )}

        <Content divider={false} className='copy-brief'>
          <ReactMarkdown
            source={entry?.copyBrief}
            renderers={{
              link: linkRenderer,
            }}
          />
        </Content>

        {entry?.postContent1 && (
          <Content divider={false}>
            <ReactMarkdown
              source={entry?.postContent1}
              renderers={{
                link: linkRenderer,
                image: responsiveImage,
              }}
            />
          </Content>
        )}

        {entry?.reusePostSections && entry?.reusePostSections.length > 0 && (
          <ReusePostSections modules={entry?.reusePostSections} />
        )}

        {entry?.postContent2 && (
          <Content divider={false}>
            <ReactMarkdown
              source={entry?.postContent2}
              renderers={{
                link: linkRenderer,
                image: responsiveImage,
              }}
            />
          </Content>
        )}

        {entry?.postPdFs && entry?.postPdFs.length > 0 && (
          <PostDownloads files={entry?.postPdFs} onClick={onPDFClick} />
        )}

        {entry?.disclaimer && (
          <Disclaimer appearance='dark' background={'#fff'} width='narrow'>
            <ReactMarkdown
              source={entry?.disclaimer}
              renderers={{
                link: linkRenderer,
              }}
            />
          </Disclaimer>
        )}

        {posts && posts.length > 0 && (
          <Posts title='Related Posts' posts={posts} />
        )}

        {entry?.webForm && (
          <CTACard
            form={
              <EmailSignup
                buttonLabel={entry?.webForm?.[0]?.firstFormButtonText}
                originCampaign={entry?.webForm?.[0]?.originCampaign}
                originWebApp={entry?.webForm?.[0]?.originWebApp}
                originWebFormCategory={
                  entry?.webForm?.[0]?.originWebFormCategory
                }
                originWebFormType={entry?.webForm?.[0]?.originWebFormType}
                originWebPageType={entry?.webForm?.[0]?.originWebPageType}
                dataExtension={entry?.webForm?.[0]?.dataExtension}
                onEmailSubmit={onEmailSubmit}
              />
            }
            disclaimer={
              <ReactMarkdown
                source={entry?.webForm?.disclaimer}
                renderers={{
                  paragraph: ({ children }) => (
                    <p className='type-caption bold color-grey1'>{children}</p>
                  ),
                  link: linkRenderer,
                }}
              />
            }
            divider={false}
          >
            <p className='type-label-large bold'>{entry?.webForm?.formIntro}</p>
            <h3 className='type-h1'>{entry?.webForm?.formHeader}</h3>
          </CTACard>
        )}
      </Styles>
    </Layout>
  );
}

ResourcePost.getInitialProps = async ({ match, req }) => {
  if (typeof window === 'undefined') {
    try {
      const queryData = getQueryParams(
        (req ? req?._parsedUrl?.search : window.location.search) || ''
      );
      const blogPost = await getBlogPost(req.url);

      return {
        data: blogPost,
        autoplay: queryData?.autoplay || false,
      };
    } catch (error) {
      return { error };
    }
  } else {
    window.location.reload();
  }
};

ResourcePost.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default ResourcePost;
